import { Dialog, Transition } from "@headlessui/react";
import { Fragment } from "react";
import { formatCurrency } from "../../utils/formatters";
import { CheckCircle } from "react-feather";
import { Button } from "../ui/button";

interface PayoutConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  brandName: string;
  amount: number;
  isSubmitting: boolean;
  /** Names of the affiliate's saved methods this brand can pay with. */
  matchingMethodNames?: string[];
}

export function PayoutConfirmationModal({
  isOpen,
  onClose,
  onConfirm,
  brandName,
  amount,
  isSubmitting,
  matchingMethodNames = [],
}: PayoutConfirmationModalProps) {
  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-card p-6 text-left align-middle shadow-xl transition-all">
                <Dialog.Title
                  as="h3"
                  className="text-lg font-medium leading-6 text-foreground mb-4"
                >
                  Confirm Payout Request
                </Dialog.Title>

                <div className="mt-2 space-y-4">
                  <p className="text-sm text-muted-foreground">
                    You are about to request a payout from{" "}
                    <strong>{brandName}</strong>
                  </p>

                  <div className="bg-muted p-4 rounded-lg space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">Amount:</span>
                      <span className="font-medium">
                        {formatCurrency(amount)}
                      </span>
                    </div>

                    <div className="flex justify-between items-center gap-4">
                      <span className="text-muted-foreground">Paid via:</span>
                      <div className="flex items-center text-right">
                        <span className="font-medium">
                          {matchingMethodNames.length > 0
                            ? matchingMethodNames.join(", ")
                            : "You'll add a method next"}
                        </span>
                        {matchingMethodNames.length > 0 && (
                          <CheckCircle size={16} className="text-green-500 ml-2 shrink-0" />
                        )}
                      </div>
                    </div>
                  </div>

                  {matchingMethodNames.length === 0 && (
                    <p className="text-sm text-muted-foreground">
                      You don't have a payout method this brand can pay with yet.
                      We'll ask for one right after you confirm.
                    </p>
                  )}
                </div>

                <div className="mt-6 flex gap-3 justify-end">
                  <button
                    type="button"
                    className="inline-flex justify-center rounded-md border border-border bg-card px-4 py-2 text-sm font-medium text-muted-foreground hover:bg-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2"
                    onClick={onClose}
                  >
                    Cancel
                  </button>
                  <Button
                    type="button"
                    onClick={onConfirm}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Processing..." : "Confirm Request"}
                  </Button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
